import { createSlice } from '@reduxjs/toolkit';
import { occyLs } from '../../../utils/localStorage';
import isObject from 'lodash/isObject';
import {
  guardDuplicateJobAction,
  guardEditJobAction,
  guardExtendJobExpiryAction,
  resolveRecruitmentRequestIdFromJobListingItem,
} from '../../../permission-system';

const lsJobData = occyLs.getObject('job_data') || {};
const lsActiveTab = occyLs.getObject('active_tab') || 0;
const lsJobTemplate = occyLs.getObject('job_Template') || 0;
const Edited = occyLs.getObject('isEdit') || false;
const lsRecruitmentRequestId = occyLs.getItem('job_creation_rr_id') || null;
const lsJobRecruitmentRequestMap = occyLs.getObject('job_creation_rr_map') || {};

const slice = createSlice({
  name: 'jobCreation',
  initialState: {
    job: { ...lsJobData },
    jobTemplate: { ...lsJobTemplate },
    activeTab: lsActiveTab,
    history: '',
    isEdit: Edited || false,
    recruitmentRequestId: lsRecruitmentRequestId,
    jobRecruitmentRequestMap: { ...lsJobRecruitmentRequestMap },
    jobTemplateId: null,
  },
  reducers: {
    setJobData: (state, { payload: { values } }) => {
      state.job = { ...values };
      occyLs.setObject('job_data', { ...values });
    },
    resetJobData: (state) => {
      state.job = {};
      occyLs.setObject('job_data', {});
    },

    resetJobInfo: (state) => {
      state.job = {};
      state.jobData = {};
      state.recruitmentRequestId = null;
      state.jobRecruitmentRequestMap = {};
      state.jobTemplateId = null;
      occyLs.setObject('job_data', {});
      occyLs.removeItem('job_creation_rr_id');
      occyLs.setObject('job_creation_rr_map', {});
    },
    setPreviousUrl: (state, { payload: { history } }) => {
      if (history) {
        state.history = { ...history };
      }
    },
    setFormData: (state, { payload: { data } }) => {
      if (!isObject(state.jobData)) {
        state.jobData = {};
      }
      if (data) {
        state.jobData = { ...data };
      }
    },
    setEditMode(state, action) {
      state.isEdit = action.payload; // true or false
      occyLs.setObject('isEdit', action.payload);
    },
    setRecruitmentRequestId(state, { payload }) {
      state.recruitmentRequestId = payload || null;
      if (payload) {
        occyLs.setItem('job_creation_rr_id', payload);
      } else {
        occyLs.removeItem('job_creation_rr_id');
      }
    },
    linkJobToRecruitmentRequest(state, { payload: { jobId, recruitmentRequestId } }) {
      if (!jobId || !recruitmentRequestId) return;
      state.jobRecruitmentRequestMap[jobId] = recruitmentRequestId;
      state.recruitmentRequestId = recruitmentRequestId;
      occyLs.setItem('job_creation_rr_id', recruitmentRequestId);
      occyLs.setObject('job_creation_rr_map', state.jobRecruitmentRequestMap);
    },
    clearRecruitmentRequestContext(state, { payload: { jobId } = {} } = {}) {
      state.recruitmentRequestId = null;
      occyLs.removeItem('job_creation_rr_id');
      if (jobId && state.jobRecruitmentRequestMap?.[jobId]) {
        delete state.jobRecruitmentRequestMap[jobId];
        occyLs.setObject('job_creation_rr_map', state.jobRecruitmentRequestMap);
      }
      if (state.jobData?.values?.recruitmentRequestId) {
        delete state.jobData.values.recruitmentRequestId;
      }
    },
    setJobTemplateId(state, { payload }) {
      state.jobTemplateId = payload || null;
    },
  },
});

export const {
  setJobData,
  setActiveTab,
  resetJobData,
  resetJobInfo,
  setFormData,
  setPreviousUrl,
  setEditMode,
  setRecruitmentRequestId,
  linkJobToRecruitmentRequest,
  clearRecruitmentRequestContext,
  setJobTemplateId,
} = slice.actions;

export default slice.reducer;

export const getJobCreationState = (state) => state.createJob.job;
export const getJobCreationFormState = (state) => state.createJob.jobData;
export const getUrlHistory = (state) => state.createJob.history;
export const getJobState = (state) => state.createJob.isEdit;
export const getRecruitmentRequestId = (state) => state.createJob.recruitmentRequestId;
export const getJobRecruitmentRequestMap = (state) =>
  state.createJob.jobRecruitmentRequestMap || {};
export const getJobTemplateId = (state) => state.createJob.jobTemplateId;

export function navigateToJobEditor(
  dispatch,
  navigate,
  { history, jobId, jhpId = '', recruitmentRequestId = null },
) {
  dispatch(setPreviousUrl({ history: { ...history } }));
  dispatch(setEditMode(true));
  if (jobId && recruitmentRequestId) {
    dispatch(linkJobToRecruitmentRequest({ jobId, recruitmentRequestId }));
  } else {
    // Avoid stale ATR ids being re-synced into the edit URL by JobCreationOutlet.
    dispatch(clearRecruitmentRequestContext({ jobId }));
  }
  const params = new URLSearchParams();
  if (jhpId) params.set('jhpId', jhpId);
  if (recruitmentRequestId) params.set('recruitmentRequestId', recruitmentRequestId);
  const query = params.toString();
  const search = query ? `?${query}` : '';
  navigate(`/jobs/${jobId}/job-information${search}`);
}

export function resolveActiveJobEditRoute(job, jhpId) {
  return { jobId: job.jobId, jhpId: jhpId ?? job?.id };
}

export function resolveDraftJobEditRoute(job, jhpId) {
  return { jobId: job?.id ?? job.jobId, jhpId: jhpId ?? '' };
}

export function createJobCardPermissionHandlers({
  runIfAllowed,
  dispatch,
  navigate,
  hst,
  resolveJobEditRoute,
  duplicateJob,
  openExtendModal,
  includeRecruitmentRequestId = true,
}) {
  return {
    handleEditJob: (job, jhpId) =>
      guardEditJobAction(
        runIfAllowed,
        () => {
          const { jobId, jhpId: hiringPlanId } = resolveJobEditRoute(job, jhpId);
          navigateToJobEditor(dispatch, navigate, {
            history: hst,
            jobId,
            jhpId: hiringPlanId,
            recruitmentRequestId: includeRecruitmentRequestId
              ? resolveRecruitmentRequestIdFromJobListingItem(job)
              : null,
          });
        },
        job,
      ),
    handleDuplicateJob: (job) =>
      guardDuplicateJobAction(runIfAllowed, job, duplicateJob),
    handleExtendJobExpiry: (job) =>
      guardExtendJobExpiryAction(runIfAllowed, job, openExtendModal),
  };
}

const listingJobCardPermissionHandlers =
  (resolveJobEditRoute, { includeRecruitmentRequestId = true } = {}) =>
  (runIfAllowed, dispatch, navigate, hst, duplicateJob, openExtendModal) =>
    createJobCardPermissionHandlers({
      runIfAllowed,
      dispatch,
      navigate,
      hst,
      resolveJobEditRoute,
      duplicateJob,
      openExtendModal,
      includeRecruitmentRequestId,
    });

// Active jobs listing: never attach recruitmentRequestId on edit.
export const createActiveJobCardPermissionHandlers =
  listingJobCardPermissionHandlers(resolveActiveJobEditRoute, {
    includeRecruitmentRequestId: false,
  });

export const createDraftJobCardPermissionHandlers =
  listingJobCardPermissionHandlers(resolveDraftJobEditRoute);
